import BTButton from "@common-components/bt-button/bt-button";
import { Box, Stack } from "@mui/material";
import { activeSubscriptionSelector } from "@common-reducers/UserSelectors";
import { openPaymentSite } from "@common-services/utils";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { useMemo } from "react";
import { getRuntimeUrl } from "@common-services/storage";
import PlanDescription from "../plan-description/plan-description";
import { ReactComponent as Crown } from './royal-crown.svg';
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

export interface UpgradeBoxParams {
    onClose?: () => void;
}

export const UpgradeBox = ({
    onClose
}: UpgradeBoxParams) => {
    const { t } = useTranslation(namespace);
    const rocketImage: string = useMemo(() => getRuntimeUrl("/img/upgrade.svg"), []);
    const lockedItem = useAppSelector(state => state.SubscriptionPopupReducer.lockedItem ?? 'messages');
    const message = useAppSelector(state => state.SubscriptionPopupReducer.message);
    const upgradeReason = useAppSelector(state => state.SubscriptionPopupReducer.upgradeReason);
    const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
    const activeSubscription = useAppSelector(activeSubscriptionSelector);
    const isLabelsLimitReason = upgradeReason === 'labels_limit' || upgradeReason === 'contact_labels_limit';

    const usageSegment = useMemo(() => {
        if (!message) return '';
        const usageMatch = message.match(/\(([^)]+)\)/);
        return usageMatch?.[1] ? ` (${usageMatch[1]})` : '';
    }, [message]);

    const plainMessage = useMemo(() => {
        if (!message) return '';
        return message.replace(/<[^>]*>/g, '').trim();
    }, [message]);

    const popupMessage = isLabelsLimitReason
        ? t('upgradeBox.labelsLimitDescription', { usage: usageSegment })
        : plainMessage;

    enum PlanNames { free, scheduler, manager, pro }

    enum FeatureNames { messages, tasks, campaigns }

    const currentPlan = PlanNames.free; // TODO: Change this on build according to the user's plan
    const nameOrEmail = "mazuznoam6@gmail.com"; // TODO: Change this on build according to the user's name or email

    interface ModalDataPlan {
        name: string;
        statuses: {
            [FeatureNames.messages]: string;
            [FeatureNames.tasks]: string;
            [FeatureNames.campaigns]: string;
        },
        image: string;
        buttonText: string;
    }

    interface ModalDataPlans {
        [PlanNames.free]: ModalDataPlan
        [PlanNames.scheduler]: ModalDataPlan
        [PlanNames.manager]: ModalDataPlan
        [PlanNames.pro]: ModalDataPlan
    }

    const modalDataPlans: ModalDataPlans = {
        [PlanNames.free]: {
            name: "Free plan",
            statuses: {
                [FeatureNames.messages]: "Up to 3 at a time",
                [FeatureNames.tasks]: "Up to 10 per contact",
                [FeatureNames.campaigns]: "Unlimited, with branding",
            },
            image: "/img/locked-free-plan.svg",
            buttonText: "Unlock all features",
        },
        [PlanNames.scheduler]: {
            name: "Scheduler plan",
            statuses: {
                [FeatureNames.messages]: "Unlimited messages!",
                [FeatureNames.tasks]: "Up to 10 per contact",
                [FeatureNames.campaigns]: "Unlimited, with branding",
            },
            image: "/img/locked-premium-plan.svg",
            buttonText: "Upgrade now",
        },
        [PlanNames.manager]: {
            name: "Manager plan",
            statuses: {
                [FeatureNames.messages]: "Unlimited messages!",
                [FeatureNames.tasks]: "Unlimited tasks!",
                [FeatureNames.campaigns]: "Unlimited, with branding",
            },
            image: "/img/locked-premium-plan.svg",
            buttonText: "Upgrade now",
        },
        [PlanNames.pro]: {
            name: "Campaigner plan",
            statuses: {
                [FeatureNames.messages]: "Unlimited messages!",
                [FeatureNames.tasks]: "Unlimited tasks!",
                [FeatureNames.campaigns]: "Unlimited without branding!",
            },
            image: "/img/locked-premium-plan.svg",
            buttonText: "Upgrade now",
        },

    }

    const modalData = {
        nameOrEmail: nameOrEmail,
        planName: modalDataPlans[currentPlan].name,
        statuses: {
            [FeatureNames.messages]: modalDataPlans[currentPlan].statuses[FeatureNames.messages],
            [FeatureNames.tasks]: modalDataPlans[currentPlan].statuses[FeatureNames.tasks],
            [FeatureNames.campaigns]: modalDataPlans[currentPlan].statuses[FeatureNames.campaigns],
        },
        image: modalDataPlans[currentPlan].image,
        buttonText: modalDataPlans[currentPlan].buttonText,
    }

    return (
        <Box sx={{
            maxWidth: 510,
            boxSizing: 'border-box',
            px: 3,
            py: 4,
            backgroundColor: 'var(--color-surface)',
            fontFamily: "'Inter', 'Poppins', 'Assistant', sans-serif",
        }}>
            <Stack alignItems="center">
                <Stack direction="row" alignItems="center" sx={{
                    fontSize: 44,
                    lineHeight: '48px',
                    color: 'var(--color-primary)',
                    textAlign: 'center',
                    mb: 2,
                    fontWeight: 700,
                }}>{t('upgradeBox.title')}</Stack>
                <Box sx={{ textAlign: 'center', mb: '60px', fontSize: 17, lineHeight: '26px', color: 'var(--color-on-surface)', opacity: 0.85 }}>
                    {popupMessage ? (
                        <Box sx={{
                            px: 2,
                            '& strong': { fontWeight: 600, color: 'var(--color-primary)' }
                        }}>
                            {popupMessage}
                        </Box>
                    ) : (
                        <>
                            Upgrade to unlock
                            <span style={{ color: 'var(--color-primary)', fontWeight: 600}}>
                                <Box sx={{
                                    display: 'inline-block',
                                    verticalAlign: 'middle',
                                    mx: '4px',
                                    position: 'relative',
                                }}>
                                    <Crown />
                                </Box>
                                unlimited
                            </span> {lockedItem} and supercharge your workflow
                        </>
                    )}
                </Box>
                <Box sx={{
                    mb: '60px'
                }}>
                    <img alt='' src={rocketImage}></img>
                </Box>
                {activeSubscription &&
                    <Box sx={{ mb: 3 }}>
                        <PlanDescription subscription={activeSubscription} />
                    </Box>
                }
                <BTButton sx={{
                    width: 1,
                }} btvariant="primary" onClick={() => openPaymentSite(accessToken ?? '')}>
                    {t('upgradeBox.upgradeButton')}
                </BTButton>
            </Stack>
        </Box>
    );
}

export default UpgradeBox;
